import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Dealer } from '../dealer/models/dealer';
import { DealerState, FetchAllDealersAction, UpdateCurrentDealerAction } from '../state/DealerState';

/**
 * Restores the in-memory/NGXS "current dealer" from the values already sitting in localStorage
 * (dealerId/userId) when a browser tab's Angular app has a valid token but hasn't hydrated the
 * dealer into state yet - e.g. a freshly opened tab, or landing directly on /authlogin while
 * already logged in elsewhere. Consumers must NOT clear localStorage as a side effect of this
 * being unable to find a match; that would invalidate every other tab sharing the same token.
 */
@Injectable({ providedIn: 'root' })
export class SessionRehydrationService {
  private readonly store = inject(Store);

  restoreDealer(): Observable<Dealer | null> {
    const currentDealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
    if (currentDealer) {
      return of(currentDealer);
    }

    const dealerId = window.localStorage.getItem('dealerId');
    const username = window.localStorage.getItem('userId') ?? '';
    if (!dealerId) {
      return of(null);
    }

    return this.store.dispatch(new FetchAllDealersAction()).pipe(
      map(() => {
        const dealers: Dealer[] = this.store.selectSnapshot(DealerState.allDealers);
        const matchedDealer = dealers?.find((d) => d.id === dealerId) ?? null;
        if (matchedDealer) {
          this.store.dispatch(new UpdateCurrentDealerAction(matchedDealer, username));
        }
        return matchedDealer;
      }),
      catchError(() => of(null))
    );
  }
}
